import { useSelector } from "react-redux";
import { useMemo } from "react";

const useNumberOfCallMinChart = () => {
  const { data = {}, status } = useSelector((state) => state.analytics);

  const { revenueChartOptionsOne, revenueChartSeriesOne } = useMemo(() => {
    if (!data?.stepData?.length || status !== "succeeded") {
      return { revenueChartOptionsOne: {}, revenueChartSeriesOne: [] };
    }

    const sortedSteps = [...data.stepData].sort(
      (a, b) => new Date(a.step) - new Date(b.step)
    );

    const formatStepLabel = (step) => {
      // ISO week format: 2025-W27
      if (/^\d{4}-W\d{2}$/.test(step)) {
        const [year, weekStr] = step.split("-W");
        const week = parseInt(weekStr, 10);
    
        // Calculate the start date of the week
        const jan4 = new Date(Date.UTC(year, 0, 4));
        const dayOfWeek = jan4.getUTCDay() || 7;
        const firstWeekStart = new Date(jan4);
        firstWeekStart.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
    
        const weekStart = new Date(firstWeekStart);
        weekStart.setUTCDate(firstWeekStart.getUTCDate() + (week - 1) * 7);
    
        const month = weekStart.toLocaleString("en-GB", { month: "long" });
    
        const firstDayOfMonth = new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), 1));
        const offset = weekStart.getUTCDate() + firstDayOfMonth.getUTCDay();
        const weekOfMonth = Math.ceil(offset / 7);
    
        return ` W${weekOfMonth} of ${month}`;
      }
    
      // Month: 2025-07
      if (/^\d{4}-\d{2}$/.test(step)) {
        const monthName = new Date(`${step}-01`).toLocaleString("en-GB", { month: "long" });
        return monthName;
      }
    
      // Day: 2025-07-01
      if (/^\d{4}-\d{2}-\d{2}$/.test(step)) {
        return new Date(step).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
        });
      }
    
      return step;
    };
    

    const dateLabels = sortedSteps.map((item) => formatStepLabel(item.step));

    const callMinutes = sortedSteps.map((item) =>
      parseFloat(item.minutes?.toFixed(2) || 0)
    );

    const revenueChartSeriesOne = [
      {
        name: "Call Minutes",
        data: callMinutes,
      },
    ];

    const revenueChartOptionsOne = {
      legend: { show: true, position: "top" },
      chart: {
        type: "area",
        height: 200,
        toolbar: { show: false },
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          opacityFrom: 0.3,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      grid: {
        borderColor: "#D1D5DB",
        strokeDashArray: 2,
      },
      markers: {
        size: 0,
        hover: { size: 6 },
      },
      xaxis: {
        categories: dateLabels,
        labels: {
          rotate: -45,
          style: { fontSize: "12px" },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => `${val.toFixed(0)}`,
          style: { fontSize: "13px" },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => `${val.toFixed(2)} mins`,
        },
      },
    };

    return {
      revenueChartOptionsOne,
      revenueChartSeriesOne,
    };
  }, [data, status]);

  return {
    revenueChartOptionsOne,
    revenueChartSeriesOne,
  };
};

export default useNumberOfCallMinChart;
